/**
 * Public SKU page for Rynora Sovereign — air-gapped / zero-egress AI for
 * regulated buyers. Positioned as a deployment tier of governed Quality
 * Intelligence: AI authoring/analysis stays inside the perimeter; the
 * deterministic quality spine does not require a commercial LLM.
 */

import { Link } from 'react-router-dom';
import { BrandMark } from '@rynora/theme';
import { ArrowLeft, Shield, ServerCog, Lock, FileCheck, Cpu, ArrowRight, Check, X } from 'lucide-react';
import '../../legal/legal.css';

const RIVAL_ROW_BG = 'rgba(148, 163, 184, 0.08)';

interface CapabilityRow {
  label: string;
  rynoraSovereign: 'yes' | 'no' | 'partial';
  rynoraSaas: 'yes' | 'no' | 'partial';
  rivalsSaas: 'yes' | 'no' | 'partial';
  note?: string;
}

const CAPABILITIES: CapabilityRow[] = [
  {
    label: 'Zero outbound DNS to commercial LLM providers',
    rynoraSovereign: 'yes',
    rynoraSaas: 'no',
    rivalsSaas: 'no',
    note: 'Rynora sovereign pins every AI feature to the on-prem Ollama daemon. No call leaves the perimeter.',
  },
  {
    label: 'BYO LLM (Ollama / vLLM / llama.cpp / private gateway)',
    rynoraSovereign: 'yes',
    rynoraSaas: 'partial',
    rivalsSaas: 'no',
    note: 'Rivals lock you to their hosted OpenAI / Anthropic pipeline. Rynora SaaS supports BYOK; Rynora sovereign supports BYO model.',
  },
  {
    label: 'FedRAMP-shaped audit trail (every AI call attributed)',
    rynoraSovereign: 'yes',
    rynoraSaas: 'yes',
    rivalsSaas: 'partial',
  },
  {
    label: 'Per-feature provider routing (intent vs RCA vs draft)',
    rynoraSovereign: 'yes',
    rynoraSaas: 'yes',
    rivalsSaas: 'no',
  },
  {
    label: 'Production-error → drafted reproduction flow (optional AI)',
    rynoraSovereign: 'yes',
    rynoraSaas: 'yes',
    rivalsSaas: 'no',
    note: 'When enabled: Sentry → Rynora ingest → LLM-drafted regression draft. Sovereign mode runs the LLM step on your private GPUs. Not required for Change Impact / FI / Release Confidence.',
  },
  {
    label: 'No SaaS dependency for the test runner',
    rynoraSovereign: 'yes',
    rynoraSaas: 'no',
    rivalsSaas: 'no',
  },
  {
    label: 'Single-tenant data plane (no neighbours)',
    rynoraSovereign: 'yes',
    rynoraSaas: 'no',
    rivalsSaas: 'no',
  },
];

function Verdict({ value }: { value: CapabilityRow['rynoraSovereign'] }) {
  if (value === 'yes') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#15803d' }}>
        <Check size={16} aria-hidden /> Yes
      </span>
    );
  }
  if (value === 'no') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#b91c1c' }}>
        <X size={16} aria-hidden /> No
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#a16207' }}>
      <ArrowRight size={16} aria-hidden /> Partial
    </span>
  );
}

export function SovereignAiPage() {
  return (
    <div className="legal-shell">
      <header className="legal-header">
        <div className="container legal-header-inner">
          <Link to="/" className="nav-logo" aria-label="Rynora — home">
            <BrandMark variant="compact" />
          </Link>
          <div className="legal-header-actions">
            <Link to="/" className="nav-auth-btn">
              <ArrowLeft size={14} /> Home
            </Link>
            <a
              className="nav-auth-btn nav-auth-btn-primary"
              href="mailto:sovereign@rynora.app?subject=Sovereign+AI+tier+enquiry"
            >
              Talk to sales
            </a>
          </div>
        </div>
      </header>

      <div className="container legal-body" style={{ gridTemplateColumns: '1fr', maxWidth: 920 }}>
        <article className="legal-article" style={{ maxWidth: 'none' }}>
          <header className="legal-article-head">
            <h1>Rynora Sovereign AI</h1>
            <p className="legal-meta">
              Governed <strong>Quality Intelligence</strong> with <strong>zero egress</strong> for AI assists — built
              for defence, finance, healthcare, and government buyers who cannot send prompts to a commercial cloud LLM.
              Execution, Change Impact, Failure Intelligence, and Release Confidence remain deterministic services
              inside your perimeter.
            </p>
          </header>

          <div className="sovereign-hero">
            <div className="sovereign-hero-pillar">
              <Shield size={20} />
              <strong>Air-gap by default</strong>
              <p>
                Every AI call terminates at an on-prem Ollama (or vLLM) daemon on your private subnet. No fallback to
                api.openai.com.
              </p>
            </div>
            <div className="sovereign-hero-pillar">
              <ServerCog size={20} />
              <strong>Your model. Your hardware.</strong>
              <p>
                Pin Llama 3.1 70B, Mixtral, Qwen 2.5, DeepSeek, or your own fine-tune. Bring your GPUs; we bring the
                orchestration.
              </p>
            </div>
            <div className="sovereign-hero-pillar">
              <FileCheck size={20} />
              <strong>Audit-trail you can show a regulator</strong>
              <p>
                Every LLM call carries an org / user / feature / provider attribution row — useful for regulated
                procurement conversations (FedRAMP-shaped, HIPAA, SOC 2) without inventing certification status.
              </p>
            </div>
          </div>

          <div className="legal-content">
            <h2>Why this exists</h2>
            <p>
              Many AI-assisted testing products pipe authoring and analysis calls through OpenAI, Anthropic, or similar
              on the public internet. That is structurally inaccessible to:
            </p>
            <ul>
              <li>
                <strong>Defence contractors</strong> working on ITAR / OFAC-classified programs that forbid data egress
                to commercial cloud providers.
              </li>
              <li>
                <strong>Pre-IPO fintech</strong> under SOC-2 + SOX-404 controls that scope every outbound DNS lookup as
                a control point.
              </li>
              <li>
                <strong>Hospital IT</strong> under HIPAA — even pseudonymised PHI cannot leave the covered entity's
                controlled network.
              </li>
              <li>
                <strong>Government agencies</strong> under FedRAMP High / IL5 (DoD) or NIST 800-171 — quarterly STIG
                attestations make commercial cloud LLMs a non-starter.
              </li>
            </ul>
            <p>
              Rynora Sovereign removes the egress dependency for AI assists. The Rynora control plane, Playwright
              runners, and (when used) the LLM provider all run inside your perimeter. Quality Intelligence —
              change-aware testing, Failure Intelligence, maintenance recommendations, and advisory Release Confidence —
              does not require a commercial cloud LLM to operate.
            </p>

            <h2>What ships in the Sovereign tier</h2>
            <ul>
              <li>
                <strong>Ollama adapter</strong> as a first-class LLM provider, alongside OpenAI / Groq / Anthropic.
                Picks up your on-prem daemon via the operator-configured <code>baseUrl</code>; authenticates via network
                policy (no API key required), or via a shared-secret Bearer token if you front Ollama with a reverse
                proxy.
              </li>
              <li>
                <strong>Per-feature routing</strong> for all 13 AI features. Pin every feature to <code>ollama</code> in
                admin-config and the SaaS provider arms are statically unreachable.
              </li>
              <li>
                <strong>Self-hosted Sentry integration</strong> for the production-feedback loop. Points the connector
                at your private Sentry instance, not at sentry.io.
              </li>
              <li>
                <strong>Air-gapped reference architecture</strong> — a single handbook page with the exact admin-config
                block, the outbound DNS allowlist, the Mermaid topology diagram, the model-pull workflow, and the
                smoke-test commands.
              </li>
            </ul>

            <h2>How Rynora Sovereign compares</h2>
            <p>
              Side-by-side, here is what your security architect can show their auditor. Every row is a question your
              procurement team has already asked.
            </p>
            <div className="sovereign-matrix-wrap" role="region" aria-label="Capability matrix">
              <table className="sovereign-matrix">
                <thead>
                  <tr>
                    <th scope="col">Capability</th>
                    <th scope="col">Rynora Sovereign</th>
                    <th scope="col">Rynora SaaS</th>
                    <th scope="col">Mabl / Functionize / testRigor</th>
                  </tr>
                </thead>
                <tbody>
                  {CAPABILITIES.map((row) => (
                    <tr key={row.label}>
                      <th scope="row">
                        <span>{row.label}</span>
                        {row.note ? <small>{row.note}</small> : null}
                      </th>
                      <td>
                        <Verdict value={row.rynoraSovereign} />
                      </td>
                      <td>
                        <Verdict value={row.rynoraSaas} />
                      </td>
                      <td>
                        <Verdict value={row.rivalsSaas} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2>The deployment footprint</h2>
            <p>
              Everything below runs inside your perimeter. The only egress is to your own package mirror (Artifactory,
              Nexus, internal ECR), used for one-time model and image pulls. No SaaS feature flags. No SaaS error
              tracker. No SaaS analytics. The full topology, with the exact admin-config block + outbound DNS allowlist,
              lives in the <a href="/docs/self-hosting">self-hosting docs</a> and the handbook reference page that ships
              with every Rynora install.
            </p>
            <div className="sovereign-footprint">
              <div>
                <Cpu size={18} />
                <strong>Control plane</strong>
                <p>Rynora API + Postgres + workers. Single Docker Compose stack. ~ 3 GB RAM.</p>
              </div>
              <div>
                <ServerCog size={18} />
                <strong>LLM tier</strong>
                <p>Ollama daemon, your GPUs. Pin llama3.1:8b for laptop demos, llama3.1:70b in production.</p>
              </div>
              <div>
                <Lock size={18} />
                <strong>Test runners</strong>
                <p>Playwright workers (Chromium / Firefox / WebKit). Drive your internal SUT directly.</p>
              </div>
            </div>

            <h2>Who this is for</h2>
            <ul>
              <li>Programs with an ATO under FedRAMP High, IL5, or NIST 800-171.</li>
              <li>SOC-2 / SOX-404 engineering orgs that scope every outbound dependency.</li>
              <li>HIPAA-covered entities testing applications that touch PHI.</li>
              <li>Anyone whose security review starts with "where does the AI call go?".</li>
            </ul>

            <h2>Who this is NOT for</h2>
            <p>
              If your security posture is "we trust OpenAI's enterprise terms", the standard Rynora SaaS tier is the
              cheaper, faster, more featureful choice. Sovereign trades managed-service convenience for control. You run
              the daemon. You bring the GPUs. You patch the model. Worth it when your auditor needs it; overkill when
              they don't.
            </p>

            <div className="sovereign-cta">
              <a
                className="nav-auth-btn nav-auth-btn-primary"
                href="mailto:sovereign@rynora.app?subject=Sovereign+AI+tier+enquiry"
              >
                Talk to sales <ArrowRight size={14} />
              </a>
              <Link to="/docs/self-hosting" className="nav-auth-btn">
                Read the self-hosting docs
              </Link>
            </div>
          </div>
        </article>
      </div>

      <footer className="legal-footer">
        <div className="container legal-footer-inner">
          <span>&copy; 2026 Rynora.</span>
          <span className="footer-bottom-links">
            <Link to="/docs">Docs</Link>
            <Link to="/security">Security</Link>
            <Link to="/contact">Contact</Link>
          </span>
        </div>
      </footer>

      <style>{`
        .sovereign-hero {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 16px;
          margin: 28px 0 36px;
        }
        .sovereign-hero-pillar {
          padding: 18px;
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          background: var(--surface);
        }
        .sovereign-hero-pillar svg { color: var(--accent); }
        .sovereign-hero-pillar strong {
          display: block;
          font-size: 15px;
          color: var(--text);
          margin-top: 6px;
        }
        .sovereign-hero-pillar p {
          font-size: 13px;
          color: var(--text-muted);
          margin: 6px 0 0;
          line-height: 1.5;
        }
        .sovereign-matrix-wrap {
          overflow-x: auto;
          margin: 12px 0 24px;
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
        }
        .sovereign-matrix {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }
        .sovereign-matrix thead th {
          text-align: left;
          padding: 12px 14px;
          background: ${RIVAL_ROW_BG};
          color: var(--text);
          font-weight: 600;
          border-bottom: 1px solid var(--border);
        }
        .sovereign-matrix tbody th {
          text-align: left;
          padding: 14px;
          font-weight: 500;
          color: var(--text);
          vertical-align: top;
          border-bottom: 1px solid var(--border);
          width: 40%;
        }
        .sovereign-matrix tbody th small {
          display: block;
          color: var(--text-muted);
          font-weight: 400;
          font-size: 12px;
          margin-top: 4px;
        }
        .sovereign-matrix td {
          padding: 14px;
          border-bottom: 1px solid var(--border);
          vertical-align: top;
        }
        .sovereign-footprint {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 12px;
          margin: 12px 0 24px;
        }
        .sovereign-footprint > div {
          padding: 14px;
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          background: var(--surface);
        }
        .sovereign-footprint svg { color: var(--accent); }
        .sovereign-footprint strong {
          display: block;
          font-size: 14px;
          color: var(--text);
          margin-top: 4px;
        }
        .sovereign-footprint p {
          font-size: 12px;
          color: var(--text-muted);
          margin: 4px 0 0;
          line-height: 1.45;
        }
        .sovereign-cta {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          margin: 28px 0 12px;
        }
      `}</style>
    </div>
  );
}
